// @ts-nocheck
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { 
  MoreVertical, 
  ChevronDown, 
  ChevronUp, 
  Mail, 
  XCircle,
  DollarSign,
  Link2,
  CheckCircle2,
  Loader2,
  Copy,
  Users
} from 'lucide-react';
import { Registration } from '@/modules/admin/hooks/useRegistrations';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface RegistrationCardProps {
  registration: Registration;
  displayTotalCents?: number;
  onViewDetails: () => void;
  onResendEmail: () => void;
  onCancel: () => void;
  onRefund: () => void;
  onMarkPaid?: () => void;
}

export function RegistrationCard({
  registration,
  displayTotalCents,
  onViewDetails,
  onResendEmail,
  onCancel,
  onRefund,
  onMarkPaid
}: RegistrationCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

  const { registrant_data, pricing_breakdown, status, payment_status } = registration;

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-500/10 text-green-700 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400';
      case 'cancelled':
        return 'bg-red-500/10 text-red-700 dark:text-red-400';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
      case 'succeeded':
        return 'bg-green-500/10 text-green-700 dark:text-green-400';
      case 'pending':
      case 'processing':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
      case 'failed':
        return 'bg-red-500/10 text-red-700 dark:text-red-400';
      case 'refunded':
        return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const generatePaymentLink = async () => {
    setGeneratingLink(true);
    try {
      // create-stripe-checkout-session builds the itemised lines and the 3%
      // card fee from the shared builder — the retired create-payment-link had
      // its own second copy of that fee.
      const { data, error } = await invokeEdgeFunction('create-stripe-checkout-session', {
        body: { registration_ids: [registration.id] }
      });

      if (error) throw error;
      if (data?.ok === false) throw new Error(data.error || 'Failed to generate payment link');

      const url = data?.checkout_url || data?.payment_url;
      if (url) {
        setPaymentLinkUrl(url);
        setIsLinkDialogOpen(true);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate payment link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyToClipboard = async () => {
    if (paymentLinkUrl) {
      await navigator.clipboard.writeText(paymentLinkUrl);
      toast.success('Payment link copied to clipboard');
    }
  };

  // Generate a short order number from the registration id (first 8 chars uppercase)
  const orderNumber = registration.id.substring(0, 8).toUpperCase();

  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="pt-6">
          {/* Order info header */}
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-3 pb-2 border-b">
            <div className="flex items-center gap-3">
              <span className="font-mono">Order #{orderNumber}</span>
              {registration.linked_registrations_count && registration.linked_registrations_count > 1 && (
                <span className="flex items-center gap-1 text-primary">
                  <Users className="h-3 w-3" />
                  {registration.linked_registrations_count} linked
                </span>
              )}
            </div>
            {registration.registered_by_name && (
              <span>Registered by: <strong>{registration.registered_by_name}</strong></span>
            )}
          </div>

          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="font-semibold text-lg">
                {registrant_data.contact_name || 'Unknown'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {registrant_data.contact_email}
              </p>
              {registrant_data.contact_phone && (
                <p className="text-sm text-muted-foreground">
                  {registrant_data.contact_phone}
                </p>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onViewDetails}>
                  View Full Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onResendEmail}>
                  <Mail className="h-4 w-4 mr-2" />
                  Resend Confirmation
                </DropdownMenuItem>
                
                {/* Payment actions for pending payments */}
                {payment_status === 'pending' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={generatePaymentLink} disabled={generatingLink}>
                      {generatingLink ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Link2 className="h-4 w-4 mr-2" />
                      )}
                      Generate Pay Link
                    </DropdownMenuItem>
                    {onMarkPaid && (
                      <DropdownMenuItem onClick={onMarkPaid} className="text-green-600">
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Mark as Paid
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                
                <DropdownMenuSeparator />
                
                {status !== 'cancelled' && (
                  <DropdownMenuItem onClick={onCancel} className="text-destructive">
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel Registration
                  </DropdownMenuItem>
                )}
                {payment_status === 'paid' && (
                  <DropdownMenuItem onClick={onRefund}>
                    <DollarSign className="h-4 w-4 mr-2" />
                    Refund Payment
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            <Badge variant="outline">
              {registration.registration_type}
            </Badge>
            <Badge className={getStatusColor(status)}>
              {status}
            </Badge>
            <Badge className={getPaymentStatusColor(payment_status)}>
              {payment_status}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <p className="text-xs text-muted-foreground">
                {registration.registration_type === 'team' ? 'Division' : 'Tier'}
              </p>
              <p className="font-medium">
                {registrant_data.division || registrant_data.pricing_tier || 'N/A'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total Amount</p>
              <p className="font-bold text-lg">
                {formatCurrency(displayTotalCents ?? pricing_breakdown?.total ?? 0)}
              </p>
            </div>
          </div>

          <div className="text-xs text-muted-foreground mb-3">
            Registered: {new Date(registration.created_at).toLocaleString()}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-2" />
                Hide Details
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-2" />
                Show Details
              </>
            )}
          </Button>

          {expanded && (
            <div className="mt-4 pt-4 border-t space-y-4">
              {/* Registration Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Order ID</p>
                  <p className="font-mono text-xs">{registration.id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Registered At</p>
                  <p>{new Date(registration.created_at).toLocaleString()}</p>
                </div>
                {(registration.registered_by_name || registration.registered_by_email) && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Registered By</p>
                    <p className="font-medium">
                      {registration.registered_by_name || 'Unknown'}
                      {registration.registered_by_email && (
                        <span className="text-muted-foreground font-normal ml-2">({registration.registered_by_email})</span>
                      )}
                    </p>
                  </div>
                )}
                {registration.order_group_id && (
                  <div>
                    <p className="text-muted-foreground">Order Group</p>
                    <p className="font-mono text-xs">{registration.order_group_id.substring(0, 8).toUpperCase()}</p>
                  </div>
                )}
              </div>

              {registrant_data.extras_selected && registrant_data.extras_selected.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Extras</p>
                  {registrant_data.extras_selected.map((extra, idx) => (
                    <div key={idx} className="text-sm flex justify-between">
                      <span>{extra.name} (x{extra.quantity})</span>
                      <span>{formatCurrency(extra.total)}</span>
                    </div>
                  ))}
                </div>
              )}

              {registrant_data.spectator_tickets && registrant_data.spectator_tickets.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Spectator Tickets</p>
                  {registrant_data.spectator_tickets.map((ticket, idx) => (
                    <div key={idx} className="text-sm flex justify-between">
                      <span>{ticket.name} (x{ticket.quantity})</span>
                      <span>{formatCurrency(ticket.total)}</span>
                    </div>
                  ))}
                </div>
              )}

              {registrant_data.special_requests?.requested && (
                <div>
                  <p className="text-sm font-medium mb-2">Special Requests</p>
                  <p className="text-sm text-muted-foreground">
                    {registrant_data.special_requests.details}
                  </p>
                  {registrant_data.special_requests.fee > 0 && (
                    <p className="text-sm mt-1">
                      Fee: {formatCurrency(registrant_data.special_requests.fee)}
                    </p>
                  )}
                </div>
              )}

              {registrant_data.discount_code && (
                <div>
                  <p className="text-sm font-medium mb-2">Discount Applied</p>
                  <div className="text-sm flex justify-between">
                    <span>Code: {registrant_data.discount_code}</span>
                    <span className="text-green-600">
                      -{formatCurrency(pricing_breakdown?.discount_amount || 0)}
                    </span>
                  </div>
                </div>
              )}

              {registration.stripe_payment_intent_id && (
                <div>
                  <p className="text-sm font-medium mb-2">Payment Details</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {registration.stripe_payment_intent_id}
                  </p>
                  {registration.payment_method && (
                    <p className="text-sm mt-1">
                      Method: {registration.payment_method}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Link Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment Link Generated</DialogTitle>
            <DialogDescription>
              Share this link with {registrant_data.contact_name || 'the registrant'} to complete payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted p-3 rounded-lg break-all text-sm">
              {paymentLinkUrl}
            </div>
            <div className="flex gap-2">
              <Button onClick={copyToClipboard} className="flex-1">
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  const subject = encodeURIComponent('Complete Your Registration Payment');
                  const body = encodeURIComponent(`Hi ${registrant_data.contact_name || ''},\n\nPlease complete your registration payment using this link:\n\n${paymentLinkUrl}\n\nThank you!`);
                  window.open(`mailto:${registrant_data.contact_email}?subject=${subject}&body=${body}`, '_blank');
                }}
                className="flex-1"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}